import { useEffect, useRef, useState } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Save, Building2, Upload, Image as ImageIcon } from "lucide-react";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    const [s, t] = await Promise.all([api.get("/settings"), api.get("/templates")]);
    setSettings(s.data);
    setTemplates(t.data);
  };
  useEffect(() => { refresh(); }, []);

  const update = (patch) => setSettings(s => ({ ...s, ...patch }));

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("image", file);
      const r = await api.post("/uploads/logo", data, { headers: { "Content-Type": "multipart/form-data" } });
      update({ logoUrl: r.data.url });
      toast.success("Logo uploaded — don't forget to click Save");
    } catch (e) {
      toast.error("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-8 max-w-3xl">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-widest mb-1">
          <Building2 size={14} /> Brand
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Brand Settings</h1>
        <p className="text-slate-400 mt-1 text-sm">Company-level values shared by every signature.</p>
      </div>

      <div className="glass p-6 space-y-4">
        <div>
          <div className="label">Active Template</div>
          <select
            className="select"
            value={settings.activeTemplateId || ""}
            onChange={e => update({ activeTemplateId: e.target.value || null })}
            data-testid="active-template-select"
          >
            <option value="">— None (cannot deploy) —</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <div className="text-[11px] text-slate-500 mt-1">The template that will be deployed to all users.</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="label">Company Name</div>
            <input className="input" value={settings.companyName || ""} onChange={e => update({ companyName: e.target.value })} data-testid="company-name" />
          </div>
          <div>
            <div className="label">Website</div>
            <input className="input" value={settings.companyWebsite || ""} onChange={e => update({ companyWebsite: e.target.value })} placeholder="https://..." data-testid="company-website" />
          </div>
          <div>
            <div className="label">Phone</div>
            <input className="input" value={settings.companyPhone || ""} onChange={e => update({ companyPhone: e.target.value })} data-testid="company-phone" />
          </div>
          <div>
            <div className="label">Logo</div>
            <div className="flex gap-2 mb-2">
              <input className="input flex-1" value={settings.logoUrl || ""} onChange={e => update({ logoUrl: e.target.value })} placeholder="Paste a URL or click Upload →" data-testid="logo-url" />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => uploadLogo(e.target.files?.[0])} data-testid="logo-upload-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn" data-testid="logo-upload-btn">
                <Upload size={13} /> {uploading ? "Uploading…" : "Upload"}
              </button>
            </div>
            {settings.logoUrl && (
              <div className="flex items-center gap-2 mt-2 p-2 rounded bg-white/[0.03] border border-white/10">
                <ImageIcon size={14} className="text-slate-500 shrink-0" />
                <img src={settings.logoUrl} alt="logo" className="h-10 w-auto max-w-[140px] object-contain rounded bg-white p-1" data-testid="logo-preview" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                <span className="text-[11px] text-slate-500 truncate">{settings.logoUrl}</span>
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="label">Company Address</div>
          <textarea className="textarea" rows={2} value={settings.companyAddress || ""} onChange={e => update({ companyAddress: e.target.value })} data-testid="company-address" />
        </div>

        <div>
          <div className="label">Tagline / Disclaimer (small grey text)</div>
          <textarea className="textarea" rows={3} value={settings.tagline || ""} onChange={e => update({ tagline: e.target.value })} placeholder="This email is confidential and may contain..." data-testid="tagline" />
        </div>

        <div className="pt-4 border-t border-white/5 flex">
          <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="save-settings">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="glass p-4 mt-4">
        <div className="text-sm font-medium mb-1">💡 Tip: Logo</div>
        <div className="text-xs text-slate-400 leading-relaxed">
          For the logo, paste either a public image URL (e.g. on your website or a CDN) or a full <code className="text-emerald-300">{`<img src="..." style="width:90px;height:auto;" alt="logo"/>`}</code> tag. Outlook does <strong>not</strong> render base64-embedded images in transport rule disclaimers — the image must be hosted at a public HTTPS URL.
        </div>
      </div>
    </div>
  );
}
